import { useFormikContext } from "formik";
import { SetStateAction, useEffect, useState } from "react";
import { useFetch } from "../../../hooks/useFetch";
import { usePlugin } from "../contexts/PluginContext";
import { getFileNameFromPath } from "../../../utils";
import { apiRoutes } from "../../../apiRoutes";
import { PluginVersion } from "../models/PluginVersion";
import { useParams } from "react-router-dom";

export const useUploadFileArea = (setIsLoading: (value: boolean) => void) => {
  const formik = useFormikContext<PluginVersion>();
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState<string | undefined>();
  const { values, setFieldValue, setValues } = formik;
  const { request } = useFetch();
  const { productDetails } = usePlugin();
  const { pluginId } = useParams() as { pluginId: string };

  useEffect(() => {
    if (!values.isNavigationLink) {
      setFileName(values?.versionUpload?.name ?? getFileNameFromPath(values?.downloadUrl ?? "") ?? "");
    }
  }, [values]);

  const handleNavigationLink = (value: boolean) => {
    if (value) {
      setFieldValue("versionUpload", undefined);
      setFieldValue("fileHash", "");
    } else setFieldValue("downloadDetails.downloadUrl", "");

    setFieldValue("isNavigationLink", value);
    setFieldValue("validateVersionUpload", !value);
  };

  const handleRemoveFile = () => {
    setFileName(undefined);
    setFieldValue("versionUpload", null);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.target.files) await extract(e.target.files[0]);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setDragOver(false);
    await extract(e.dataTransfer.files[0]);
  };

  const extract = async (file: File) => {
    const data = new FormData();
    data.set("versionUpload", file);

    setFileName(file?.name);
    setIsLoading(true);

    const onSuccess = async (response: Response) => {
      const json = await response.json();

      setValues({
        ...values,
        versionUpload: file,
        versionNumber: json.version,
        fileHash: json.checksum,
        minimumRequiredVersionOfStudio: json.requiredProduct.minimumStudioVersion,
        maximumRequiredVersionOfStudio: json.requiredProduct.maximumStudioVersion,
        supportedProducts: productDetails
          .filter(
            (x) =>
              x.minimumStudioVersion === json.requiredProduct.minimumStudioVersion &&
              x.productName.includes("Trados Studio")
          )
          .map((x) => x.id)
      } as SetStateAction<PluginVersion>);
    };

    await request({
      route: apiRoutes.pluginVersion.extractVersionFromManifest(parseInt(pluginId)),
      method: "POST",
      body: data,
      onSuccess,
      onAny: () => setIsLoading(false)
    });
  };

  return {
    fileName,
    dragOver,
    handleNavigationLink,
    handleRemoveFile,
    handleFileChange,
    handleDragLeave,
    handleDragOver,
    handleDrop
  };
};
